import { useCallback, useEffect, useRef, useState } from 'react';
import { AttachmentsPanel, type PdfStatusMessage } from './components/AttachmentsPanel';
import { PdfSigner } from './components/PdfSigner';
import { SignatureSetup } from './components/SignatureSetup';
import { SignatureView } from './components/SignatureView';
import { StatusPanel } from './components/StatusPanel';
import { acquireGraphToken, AuthError } from './services/auth';
import { getDrive, getMe, GraphError } from './services/graph';
import {
  deleteSignaturePng,
  downloadSignaturePng,
  ensureSignatureFolder,
  signatureExists,
  uploadSignaturePng,
  uploadSignedPdf,
} from './services/onedrive';
import {
  getAttachmentBytes,
  isComposeMode,
  listSignableAttachments,
  openReplyWithSignedPdf,
  replaceComposeAttachmentWithSigned,
  waitForOfficeReady,
  type SignableAttachment,
} from './office/outlook';
import { logError } from './services/logger';
import { blobToObjectUrl, revokeObjectUrl } from './utils/png';
import { trimPngWhitespace } from './utils/trimPng';
import {
  initialStatus,
  type AppStatus,
  type Phase2View,
  type SigningSession,
} from './types';
import './components/MainPanel.css';

export default function App() {
  const [status, setStatus] = useState<AppStatus>(initialStatus);
  const [view, setView] = useState<Phase2View>('booting');
  const [runId, setRunId] = useState(0);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [signaturePngBytes, setSignaturePngBytes] = useState<Uint8Array | null>(null);
  const [sigBusy, setSigBusy] = useState(false);
  const [sigError, setSigError] = useState<string | null>(null);
  const [docs, setDocs] = useState<SignableAttachment[]>([]);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [pdfMessage, setPdfMessage] = useState<PdfStatusMessage | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [signing, setSigning] = useState<SigningSession | null>(null);
  const [signBusy, setSignBusy] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const patch = useCallback((partial: Partial<AppStatus>) => {
    setStatus((s) => ({ ...s, ...partial }));
  }, []);

  const setPreviewFromBlob = useCallback(async (blob: Blob) => {
    const trimmed = await trimPngWhitespace(blob);
    const trimmedBlob = new Blob([new Uint8Array(trimmed)], { type: 'image/png' });
    setSignaturePngBytes(trimmed);
    const url = blobToObjectUrl(trimmedBlob);
    revokeObjectUrl(previewUrlRef.current);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  const clearPreview = useCallback(() => {
    revokeObjectUrl(previewUrlRef.current);
    previewUrlRef.current = null;
    setPreviewUrl(null);
    setSignaturePngBytes(null);
  }, []);

  useEffect(() => {
    return () => {
      revokeObjectUrl(previewUrlRef.current);
    };
  }, []);

  const loadDocs = useCallback(async () => {
    try {
      const list = await listSignableAttachments();
      setDocs(list);
    } catch (e) {
      logError(e);
      setDocs([]);
    }
  }, []);

  const afterSignatureReady = useCallback(
    async (token: string) => {
      clearPreview();
      const blob = await downloadSignaturePng(token);
      const trimmed = await trimPngWhitespace(blob);
      const originalSize = blob.size;
      // Persist cropped signature so Word/PDF embeds stay tight
      if (trimmed.byteLength < originalSize * 0.92) {
        try {
          await uploadSignaturePng(
            token,
            new Blob([new Uint8Array(trimmed)], { type: 'image/png' }),
          );
        } catch (e) {
          logError('Trimmed signature could not be saved back to OneDrive', e);
        }
      }
      await setPreviewFromBlob(new Blob([new Uint8Array(trimmed)], { type: 'image/png' }));
      setView('main');
      setSigError(null);
      await loadDocs();
    },
    [clearPreview, loadDocs, setPreviewFromBlob],
  );

  const runChecks = useCallback(async () => {
    setView('booting');
    setAccessToken(null);
    clearPreview();
    setSigError(null);
    setPdfMessage(null);
    setPdfError(null);
    setDocs([]);
    setSigning(null);
    setSignError(null);
    setStatus({ ...initialStatus(), officeReady: 'running' });

    try {
      await waitForOfficeReady();
      patch({ officeReady: 'ok', auth: 'running' });

      const { accessToken: token, method } = await acquireGraphToken();
      setAccessToken(token);
      patch({ auth: 'ok', authMethod: method, user: 'running' });

      const graphUser = await getMe(token);
      patch({ user: 'ok', graphUser, onedrive: 'running' });

      await getDrive(token);
      patch({ onedrive: 'ok', folder: 'running' });

      const folder = await ensureSignatureFolder(token);
      patch({
        folder: 'ok',
        folderAction: folder.action,
        error: null,
        errorDetail: null,
      });

      const exists = await signatureExists(token);
      if (!exists) {
        setView('setup');
        return;
      }
      await afterSignatureReady(token);
    } catch (e) {
      logError(e);
      let message = 'Unbekannter Fehler.';
      let detail: string | null = null;

      if (e instanceof AuthError || e instanceof GraphError) {
        message = e.message;
        detail = e.detail || null;
      } else if (e instanceof Error) {
        message = e.message;
      } else {
        detail = String(e);
      }

      setView('error');
      setStatus((s) => ({
        ...s,
        error: message,
        errorDetail: detail,
        officeReady: s.officeReady === 'running' ? 'error' : s.officeReady,
        auth: s.auth === 'running' ? 'error' : s.auth,
        user: s.user === 'running' ? 'error' : s.user,
        onedrive: s.onedrive === 'running' ? 'error' : s.onedrive,
        folder: s.folder === 'running' ? 'error' : s.folder,
      }));
    }
  }, [afterSignatureReady, clearPreview, patch]);

  useEffect(() => {
    void runChecks();
  }, [runId, runChecks]);

  const withToken = useCallback(async (): Promise<string> => {
    if (accessToken) return accessToken;
    const { accessToken: token } = await acquireGraphToken();
    setAccessToken(token);
    return token;
  }, [accessToken]);

  const saveSignature = useCallback(
    async (file: File) => {
      setSigBusy(true);
      setSigError(null);
      try {
        const token = await withToken();
        const trimmed = await trimPngWhitespace(file);
        await uploadSignaturePng(
          token,
          new Blob([new Uint8Array(trimmed)], { type: 'image/png' }),
        );
        await afterSignatureReady(token);
      } catch (e) {
        logError(e);
        setSigError(e instanceof Error ? e.message : String(e));
      } finally {
        setSigBusy(false);
      }
    },
    [afterSignatureReady, withToken],
  );

  const deleteSignature = useCallback(async () => {
    setSigBusy(true);
    setSigError(null);
    try {
      const token = await withToken();
      await deleteSignaturePng(token);
      clearPreview();
      setView('setup');
      setDocs([]);
      setPdfMessage(null);
      setSigning(null);
    } catch (e) {
      logError(e);
      setSigError(e instanceof Error ? e.message : String(e));
    } finally {
      setSigBusy(false);
    }
  }, [clearPreview, withToken]);

  const onSignDoc = useCallback(async (att: SignableAttachment) => {
    setPdfBusyId(att.id);
    setPdfMessage(null);
    setPdfError(null);
    setSignError(null);
    try {
      if (!signaturePngBytes || !previewUrl) {
        throw new Error('Keine Unterschrift geladen. Bitte Task Pane neu öffnen.');
      }
      const documentBytes = await getAttachmentBytes(att.id, att.name);
      setSigning({
        attachmentId: att.id,
        fileName: att.name,
        kind: att.kind,
        documentBytes,
      });
      setView('signing');
    } catch (e) {
      logError(e);
      setPdfError(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfBusyId(null);
    }
  }, [previewUrl, signaturePngBytes]);

  const downloadLocal = (bytes: Uint8Array, name: string) => {
    const lower = name.toLowerCase();
    const type = lower.endsWith('.docx')
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : 'application/pdf';
    const blob = new Blob([new Uint8Array(bytes)], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onApplySigned = useCallback(
    async (signedPdf: Uint8Array, outName: string) => {
      setSignBusy(true);
      setSignError(null);
      try {
        const token = await withToken();
        // Archiv: persönliches OneDrive /SignatureApp/{name}-signiert.pdf
        const saved = await uploadSignedPdf(token, outName, signedPdf);

        // Compose: signierte Datei anhängen, unsigniertes Original entfernen
        const compose = isComposeMode();
        let attachedHere = false;
        let replyNote = '';

        if (compose && signing?.attachmentId) {
          const replaced = await replaceComposeAttachmentWithSigned({
            originalAttachmentId: signing.attachmentId,
            signedFileName: outName,
            signedBytes: signedPdf,
          });
          attachedHere = replaced.attached;
          if (replaced.attached && replaced.removedOriginal) {
            replyNote = ' Original-Anhang entfernt.';
          } else if (replaced.attached && !replaced.removedOriginal) {
            replyNote = ` ${replaced.detail || ''}`;
          } else {
            downloadLocal(signedPdf, outName);
            replyNote =
              ' Konnte nicht automatisch anhängen (Outlook-Rechte). Datei wurde heruntergeladen — bitte manuell anhängen.';
          }
          await loadDocs();
        } else if (compose) {
          downloadLocal(signedPdf, outName);
          replyNote =
            ' Kein Original-Anhang gefunden. Datei wurde heruntergeladen — bitte manuell anhängen.';
        } else {
          // Read-Modus: Antwortmail mit signiertem Dokument als Anhang öffnen
          const reply = await openReplyWithSignedPdf(outName, signedPdf);
          if (reply.opened && reply.withAttachment) {
            replyNote = ' Antwortmail mit Anhang geöffnet.';
          } else if (reply.opened) {
            replyNote = ` ${reply.detail || 'Antwort geöffnet.'}`;
            downloadLocal(signedPdf, outName);
          } else {
            replyNote = ` ${reply.detail || ''} Datei wurde lokal heruntergeladen.`;
            downloadLocal(signedPdf, outName);
          }
        }

        setSigning(null);
        setView('main');
        const kb = Math.max(1, Math.round(signedPdf.byteLength / 1024));
        setPdfMessage({
          text: attachedHere
            ? `${saved.name} signiert (${kb} KB) und an die Mail angehängt.${replyNote}`
            : `${saved.name} signiert (${kb} KB).${replyNote}`,
          oneDriveUrl: saved.webUrl,
          oneDriveLabel: saved.webUrl
            ? `OneDrive öffnen: /SignatureApp/${saved.name}`
            : undefined,
        });
      } catch (e) {
        logError(e);
        setSignError(e instanceof Error ? e.message : String(e));
      } finally {
        setSignBusy(false);
      }
    },
    [withToken, signing, loadDocs],
  );

  const retry = () => setRunId((n) => n + 1);

  if (view === 'booting') {
    return (
      <div className="mp">
        <div className="mp-loading">
          <div className="mp-spinner" aria-hidden />
          <span>Wird geladen…</span>
        </div>
      </div>
    );
  }

  if (view === 'error') {
    return <StatusPanel status={status} onRetry={retry} />;
  }

  if (view === 'signing' && signing && signaturePngBytes && previewUrl) {
    return (
      <div className="mp">
        <header className="mp-header">
          <div className="sp-logo" aria-hidden>
            <img src="/logo-256.png" alt="" width={44} height={44} />
          </div>
          <div>
            <h1 className="sp-title">Signatur</h1>
            <p className="sp-subtitle">PDF signieren · Build 2.1.4</p>
          </div>
        </header>
        <div className="mp-divider" />
        <PdfSigner
          fileName={signing.fileName}
          kind={signing.kind}
          documentBytes={signing.documentBytes}
          signaturePngUrl={previewUrl}
          signaturePngBytes={signaturePngBytes}
          busy={signBusy}
          error={signError}
          onCancel={() => {
            setSigning(null);
            setSignError(null);
            setView('main');
          }}
          onApply={onApplySigned}
        />
      </div>
    );
  }

  return (
    <div className="mp">
      <header className="mp-header">
        <div className="sp-logo" aria-hidden>
          <img src="/logo-256.png" alt="" width={44} height={44} />
        </div>
        <div>
          <h1 className="sp-title">Signatur</h1>
          <p className="sp-subtitle">Build 2.1.4</p>
        </div>
      </header>

      <div className="mp-divider" />

      {view === 'setup' ? (
        <SignatureSetup onSave={saveSignature} busy={sigBusy} error={sigError} />
      ) : (
        <>
          <SignatureView
            previewUrl={previewUrl}
            busy={sigBusy}
            error={sigError}
            onChange={saveSignature}
            onDelete={deleteSignature}
          />
          <div className="mp-divider" />
          <AttachmentsPanel
            attachments={docs}
            busyId={pdfBusyId}
            message={pdfMessage}
            error={pdfError}
            onSign={(att) => void onSignDoc(att)}
          />
        </>
      )}

      {import.meta.env.DEV ? <StatusPanel status={status} onRetry={retry} compact /> : null}
    </div>
  );
}
